import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Pencil, Plus, Sparkles, Trash2, ArrowRight } from 'lucide-react'

import {
  createCanvasBoard,
  deleteCanvasBoard,
  getProduction,
  listCanvasBoards,
  patchCanvasBoard,
  type CanvasBoard,
  type Production,
} from '@/api/studio-production'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogBadge } from '@/shared/components/catalog/CatalogBadge'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar, CatalogListPrimaryButton } from '@/shared/components/catalog/CatalogListToolbar'
import { PageEmptyState, PageEmptyStateActionButton } from '@/shared/components/PageEmptyState'
import { StudioCatalogCard } from '@/features/studio/components/StudioCatalogCard'
import { canvasBoardPath } from '@/features/studio/lib/canvas-path'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { formatResolvedApiError, resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, protoBtnClassName } from '@/features/studio/components/ui-kit'
import { CanvasBoardModal } from '@/features/studio/modals/CanvasBoardModal'
import { useProductionPermissions } from '@/features/studio/hooks/useProductionPermissions'

function formatUpdatedAt(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function CanvasBoardListPage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const base = productionBasePath(productionId)
  const returnPath = `${base}/canvas-boards`

  const [items, setItems] = useState<CanvasBoard[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CanvasBoard | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CanvasBoard | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [production, setProduction] = useState<Production | null>(null)
  const { canDelete } = useProductionPermissions(production)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [boardRes, prod] = await Promise.all([
        listCanvasBoards(productionId),
        getProduction(productionId).catch(() => null),
      ])
      setItems(boardRes.items)
      setProduction(prod)
    } catch (e) {
      setError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'canvasBoards.loadFailed')))
    } finally {
      setLoading(false)
    }
  }, [productionId, t])

  useEffect(() => {
    void reload()
  }, [reload])

  function openCreateModal() {
    setEditing(null)
    setModalOpen(true)
  }

  async function handleSave(title: string) {
    if (editing) {
      await patchCanvasBoard(productionId, editing.id, title)
    } else {
      await createCanvasBoard(productionId, title)
    }
    setModalOpen(false)
    setEditing(null)
    await reload()
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    setDeleteError(null)
    try {
      await deleteCanvasBoard(productionId, deleteTarget.id)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      setDeleteError(formatResolvedApiError(t, resolveApiErrorMessage(e, 'canvasBoards.deleteFailed')))
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <>
      <CatalogListPageShell>
        <CatalogListToolbar
          actions={
            <CatalogListPrimaryButton disabled={loading} onClick={openCreateModal}>
              <Plus className="h-4 w-4" />
              {t('canvasBoards.create')}
            </CatalogListPrimaryButton>
          }
        />

        {error ? (
          <p className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-300">
            {error}
          </p>
        ) : null}

        {loading ? (
          <CatalogListSkeleton />
        ) : items.length === 0 ? (
        <PageEmptyState
          icon={Sparkles}
          tone="indigo"
          title={t('canvasBoards.emptyTitle')}
          subtitle={t('canvasBoards.emptyHint')}
        >
          <PageEmptyStateActionButton onClick={openCreateModal}>
            <Plus className="h-4 w-4" />
            {t('canvasBoards.create')}
          </PageEmptyStateActionButton>
        </PageEmptyState>
      ) : (
        <ul className={CATALOG_LIST_GRID_CLASS}>
          {items.map((board) => (
            <StudioCatalogCard
              key={`${board.scope_type}:${board.scope_key}`}
              href={canvasBoardPath(productionId, board, returnPath)}
              title={board.title}
              description={t('canvasBoards.rowHint')}
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-2 text-indigo-500/35">
                  <Sparkles size={36} strokeWidth={1.25} aria-hidden />
                </div>
              }
              badges={
                board.is_default ? (
                  <CatalogBadge tone="indigo" className="absolute left-3 top-3 z-[2]">
                    {t('canvasBoards.defaultBadge')}
                  </CatalogBadge>
                ) : undefined
              }
              meta={<span>{formatUpdatedAt(board.updated_at)}</span>}
              bottomActions={
                <>
                  <ProtoBtn
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setEditing(board)
                      setModalOpen(true)
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    {t('canvasBoards.rename')}
                  </ProtoBtn>
                  <Link
                    to={canvasBoardPath(productionId, board, returnPath)}
                    className={protoBtnClassName({ size: 'sm', variant: 'soft', tone: 'amber' })}
                  >
                    <ArrowRight className="h-3.5 w-3.5" />
                    {t('canvasBoards.open')}
                  </Link>
                  {!board.is_default && canDelete ? (
                    <ProtoBtn size="sm" variant="danger" onClick={() => setDeleteTarget(board)}>
                      <Trash2 className="h-3.5 w-3.5" />
                      {t('common.delete')}
                    </ProtoBtn>
                  ) : null}
                </>
              }
            />
          ))}
        </ul>
      )}
      </CatalogListPageShell>

      <CanvasBoardModal
        open={modalOpen}
        initialTitle={editing?.title ?? ''}
        isEdit={editing != null}
        onClose={() => {
          setModalOpen(false)
          setEditing(null)
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={deleteTarget != null}
        title={t('canvasBoards.deleteTitle')}
        message={t('canvasBoards.deleteConfirm', { name: deleteTarget?.title ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void handleDelete()}
        onCancel={() => {
          setDeleteTarget(null)
          setDeleteError(null)
        }}
      />
    </>
  )
}
